/**
 * The reminder bar for tasks (12 Oct 2026), on every page but Tasks itself:
 * the owner is told what waits for his check, an employee what is overdue or
 * due today — and, since employees give tasks too (20 Oct 2026), what they
 * gave that now waits for their check. Gone when there is nothing to say.
 */
import { Link, useLocation } from 'react-router-dom';
import { ListChecks } from 'lucide-react';

import { formatNumber } from '@/lib/format';
import { useTaskSummary } from './tasks-api';

const plural = (n: number, one: string, many: string) => `${formatNumber(n)} ${n === 1 ? one : many}`;

export function TasksNudge() {
  const location = useLocation();
  const summary = useTaskSummary().data;
  if (!summary?.role || location.pathname === '/tasks') return null;
  let text: string | null = null;
  // Where the bar leads: the person's own check lives under "I gave".
  let to = '/tasks';
  if (summary.role === 'assigner') {
    if (summary.waiting > 0) text = `${plural(summary.waiting, 'task is', 'tasks are')} waiting for your check.`;
  } else {
    const mine = [
      summary.overdue > 0 ? `${plural(summary.overdue, 'task', 'tasks')} overdue` : null,
      summary.dueToday > 0 ? `${plural(summary.dueToday, 'task', 'tasks')} due today` : null,
    ].filter((part): part is string => part !== null);
    const check = summary.givenWaiting > 0 ? `${plural(summary.givenWaiting, 'task you gave is', 'tasks you gave are')} waiting for your check` : null;
    const parts = check ? [...mine, check] : mine;
    if (parts.length) text = `${parts.join(' · ')}.`;
    if (check && mine.length === 0) to = '/tasks?view=given';
  }
  if (!text) return null;
  const urgent = summary.role === 'assignee' && summary.overdue > 0;
  return (
    <Link
      to={to}
      className="mb-3 flex items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-caption text-ink-2 hover:border-accent"
    >
      <ListChecks className={urgent ? 'h-4 w-4 shrink-0 text-signal' : 'h-4 w-4 shrink-0 text-accent'} aria-hidden />
      <span className="min-w-0 flex-1">{text}</span>
      <span className="shrink-0 font-medium text-accent">Open</span>
    </Link>
  );
}
